/**
 * Module helpers — id minting + parameter serialization. Shared by every
 * concrete SynthModule so they're consistent on the wire.
 */

import type { AnyParameter, ParamSource } from '../../params/Parameter';

/** Compact unique-ish id, prefixed by module type for legibility in presets. */
export function mintId(prefix: string): string {
  const short =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${short}`;
}

/** Write a parameter list to a flat name→value map. */
export function writeParamState(
  params: readonly AnyParameter[],
): Record<string, number | string> {
  const out: Record<string, number | string> = {};
  for (const p of params) {
    out[p.name] = p.value;
  }
  return out;
}

/**
 * Apply a flat name→value map to a parameter list. Unknown params are ignored
 * (presets survive minor module changes). Source tag defaults to 'preset'.
 */
export function readParamState(
  params: readonly AnyParameter[],
  state: Record<string, number | string>,
  source: ParamSource = 'preset',
): void {
  for (const p of params) {
    const raw = state[p.name];
    if (raw === undefined) continue;
    if (p.kind === 'numeric') {
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        p.set(raw, source);
      }
    } else {
      if (typeof raw === 'string') {
        // EnumParameter<string>.set refuses unknown values silently, which is
        // what we want — bad preset data shouldn't throw.
        p.set(raw, source);
      }
    }
  }
}
